import React, { useState, useEffect, useMemo } from "react";
import DefaultLayout from "@/Layouts/DefaultLayout";
import { Link, useForm, usePage } from "@inertiajs/react";
import Breadcrumb from "@/Components/Breadcrumbs/Breadcrumb";
import NotificationsOne from "@/Components/Notifications/NotificationsOne";
import InputError from "@/Components/InputError";
import PhoneInput from "@/Components/PhoneInput";
import axios from "axios";

import LogoDark from "../../images/logo/logo-dark.svg";
import Logo from "../../images/logo/logo.svg";

export default function Register() {
    const { props } = usePage();
    const { flash } = props;
    const { appName, globalSettings, buckets, tenantSlug } = props;
    const peopleFollowupEnabled = Boolean(props.peopleFollowupEnabled);
    const claimSmsAvailable = Boolean(
        props.claimSmsAvailable ?? props.twilioOtpEnabled,
    );
    const [showMessage, setMessage] = useState(false);
    const [dobError, setDobError] = useState("");
    const [claimError, setClaimError] = useState("");
    const [accountExistsMessage, setAccountExistsMessage] = useState("");
    const [matchToken, setMatchToken] = useState(null);
    const [claimToken, setClaimToken] = useState(null);
    const [claimHint, setClaimHint] = useState("");
    const [claimChannels, setClaimChannels] = useState([]);
    const [otpChannel, setOtpChannel] = useState("email");
    const [otpCode, setOtpCode] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [claimBusy, setClaimBusy] = useState(false);
    const [claimStep, setClaimStep] = useState(null); // null | 'found' | 'verified'

    const staticAssetsBucket = buckets["static_assets"].name;
    const profileImagesRegion = buckets["profile_images"].region;
    const logoBaseUrl = `https://${staticAssetsBucket}.s3.${profileImagesRegion}.amazonaws.com/${tenantSlug}/`;

    const { data, setData, post, processing, errors, reset } = useForm({
        username: "",
        email: "",
        password: "",
        password_confirmation: "",
        first_name: "",
        last_name: "",
        dob: "",
        phone_number: "",
        is_enabled:
            globalSettings.is_invite_only === null
                ? false
                : !globalSettings.is_invite_only,
        newsletter: true,
    });

    const availableChannels = useMemo(() => {
        const channels = Array.isArray(claimChannels) ? claimChannels : [];
        if (!claimSmsAvailable) {
            return channels.filter((c) => c === "email");
        }
        return channels;
    }, [claimChannels, claimSmsAvailable]);

    function isAtLeast13YearsOld(dateString) {
        if (!dateString) return false;
        const today = new Date();
        const dob = new Date(dateString);
        const age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            return age - 1 >= 13;
        }
        return age >= 13;
    }

    function resetClaimState() {
        setMatchToken(null);
        setClaimToken(null);
        setClaimHint("");
        setClaimChannels([]);
        setOtpChannel("email");
        setOtpCode("");
        setOtpSent(false);
        setClaimStep(null);
        setClaimError("");
        setAccountExistsMessage("");
    }

    function csrfHeaders() {
        const token =
            props.csrf_token ||
            document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content");
        return token ? { "X-CSRF-TOKEN": token } : {};
    }

    async function checkExistingContact() {
        const { data: result } = await axios.post(
            route("signup.check-existing"),
            {
                email: data.email || null,
                phone_number: data.phone_number || null,
            },
            { headers: csrfHeaders() },
        );
        return result;
    }

    async function sendClaimOtp() {
        setClaimBusy(true);
        setClaimError("");
        try {
            const { data: result } = await axios.post(
                route("signup.send-otp"),
                {
                    match_token: matchToken,
                    channel: otpChannel,
                },
                { headers: csrfHeaders() },
            );
            setOtpSent(true);
            if (result.hint) {
                setClaimHint(result.hint);
            }
        } catch (err) {
            const message =
                err?.response?.data?.errors?.channel?.[0] ||
                err?.response?.data?.message ||
                "Unable to send verification code.";
            setClaimError(message);
        } finally {
            setClaimBusy(false);
        }
    }

    async function verifyClaimOtp() {
        setClaimBusy(true);
        setClaimError("");
        try {
            const { data: result } = await axios.post(
                route("signup.verify-otp"),
                {
                    match_token: matchToken,
                    otp: otpCode,
                },
                { headers: csrfHeaders() },
            );
            setClaimToken(result.claim_token);
            setClaimStep("verified");
        } catch (err) {
            const message =
                err?.response?.data?.errors?.otp?.[0] ||
                err?.response?.data?.message ||
                "Invalid verification code.";
            setClaimError(message);
        } finally {
            setClaimBusy(false);
        }
    }

    async function submitClaim() {
        setClaimBusy(true);
        setClaimError("");
        try {
            const { data: result } = await axios.post(
                route("signup.claim"),
                {
                    claim_token: claimToken,
                    username: data.username,
                    email: data.email,
                    password: data.password,
                    password_confirmation: data.password_confirmation,
                    first_name: data.first_name,
                    last_name: data.last_name,
                    dob: data.dob,
                    phone_number: data.phone_number || null,
                    is_enabled: data.is_enabled,
                    newsletter: data.newsletter,
                },
                { headers: csrfHeaders() },
            );

            if (result.redirect) {
                window.location.href = result.redirect;
                return;
            }

            resetClaimState();
            reset(
                "username",
                "email",
                "password",
                "password_confirmation",
                "first_name",
                "last_name",
                "dob",
                "phone_number",
            );
        } catch (err) {
            const errorsBag = err?.response?.data?.errors || {};
            const first =
                Object.values(errorsBag).flat()?.[0] ||
                err?.response?.data?.message ||
                "Unable to claim profile.";
            setClaimError(first);
        } finally {
            setClaimBusy(false);
        }
    }

    const submit = async (e) => {
        e.preventDefault();

        if (!isAtLeast13YearsOld(data.dob)) {
            setDobError("You must be at least 13 years old to register.");
            return;
        }
        setDobError("");

        if (peopleFollowupEnabled && claimStep === "verified" && claimToken) {
            await submitClaim();
            return;
        }

        if (peopleFollowupEnabled && claimStep === "found") {
            setClaimError(
                "Please verify your identity to claim your existing profile.",
            );
            return;
        }

        if (peopleFollowupEnabled) {
            setClaimBusy(true);
            setClaimError("");
            setAccountExistsMessage("");
            try {
                const result = await checkExistingContact();

                if (result.status === "account_exists") {
                    setAccountExistsMessage(
                        result.message ||
                            "An account already exists for this contact. Please sign in or reset your password.",
                    );
                    return;
                }

                if (result.status === "claim_required") {
                    setMatchToken(result.match_token);
                    setClaimHint(result.hint || "");
                    setClaimChannels(result.channels || []);
                    const channels = result.channels || [];
                    const preferred = channels.includes("email")
                        ? "email"
                        : channels[0] || "email";
                    setOtpChannel(preferred);
                    setOtpSent(false);
                    setOtpCode("");
                    setClaimToken(null);
                    setClaimStep("found");
                    return;
                }
            } catch (err) {
                const message =
                    err?.response?.data?.errors?.email?.[0] ||
                    err?.response?.data?.message ||
                    "Unable to check existing contacts.";
                setClaimError(message);
                return;
            } finally {
                setClaimBusy(false);
            }
        }

        post(route("register"), {
            data: {
                ...data,
            },
            onSuccess: () => {
                reset(
                    "username",
                    "email",
                    "password",
                    "password_confirmation",
                    "first_name",
                    "last_name",
                    "dob",
                    "phone_number",
                );
            },
        });
    };

    useEffect(() => {
        if (flash.success || flash.error) {
            setMessage(true);
        }
    }, [flash]);

    const formBusy = processing || claimBusy;
    const submitLabel =
        claimStep === "verified"
            ? "Claim profile & create account"
            : claimStep === "found"
              ? "Continue claim"
              : "Create account";

    return (
        <DefaultLayout>
            <div className="mx-auto max-w-screen-2xl   ">
                <Breadcrumb
                    pageName="Sign Up"
                    pageLink="/"
                    pageLinkName="Home"
                />

                <NotificationsOne
                    show={showMessage}
                    message={flash.error || flash.success}
                    toggleShow={setMessage}
                    type={flash.error ? "error" : "success"}
                />

                <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="flex flex-wrap items-center">
                        <div className="hidden w-full xl:block xl:w-1/2">
                            <div className="px-26 py-17.5 text-center">
                                <div className="mb-5.5 inline-block">
                                    <img
                                        className="hidden dark:block w-80"
                                        src={
                                            globalSettings.logo_dark_file_name ===
                                            null
                                                ? Logo
                                                : logoBaseUrl +
                                                  globalSettings.logo_dark_file_name
                                        }
                                        alt="Logo"
                                    />

                                    <div className="mb-5.5 flex items-center space-x-4">
                                        <img
                                            className="dark:hidden w-80"
                                            src={
                                                globalSettings.logo_dark_file_name ===
                                                null
                                                    ? LogoDark
                                                    : logoBaseUrl +
                                                      globalSettings.logo_file_name
                                            }
                                            alt="Logo"
                                        />
                                    </div>
                                </div>

                                <p className="font-bold text-black dark:text-white">
                                    Welcome! Let’s get you set up
                                </p>
                                <p className="2xl:px-20">
                                    Just a few details and you’re in.
                                </p>
                            </div>
                        </div>

                        <div className="w-full border-stroke dark:border-strokedark xl:w-1/2 xl:border-l-2">
                            <div className="w-full p-4 sm:p-12.5 xl:p-17.5">
                                <span className="mb-1.5 block font-medium">
                                    Start for free
                                </span>
                                <h2 className="mb-9 text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
                                    Sign Up to {appName}
                                </h2>

                                {accountExistsMessage && (
                                    <div className="mb-6 rounded-lg border border-stroke bg-gray-50 p-4 text-sm text-black dark:border-strokedark dark:bg-meta-4 dark:text-white">
                                        <p>{accountExistsMessage}</p>
                                        <p className="mt-2">
                                            <Link
                                                href={route("password.request")}
                                                className="text-primary"
                                            >
                                                Reset your password
                                            </Link>
                                            {" · "}
                                            <Link
                                                href="/login"
                                                className="text-primary"
                                            >
                                                Sign in
                                            </Link>
                                        </p>
                                    </div>
                                )}

                                {claimStep && (
                                    <div className="mb-6 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm text-black dark:text-white">
                                        <p className="mb-2 font-semibold">
                                            We found you!
                                        </p>
                                        {claimHint && (
                                            <p className="mb-3 text-body">
                                                We will send a code to the
                                                contact on file:{" "}
                                                <span className="font-medium">
                                                    {claimHint}
                                                </span>
                                            </p>
                                        )}

                                        {claimStep === "found" && (
                                            <>
                                                {availableChannels.length >
                                                    1 && (
                                                    <div className="mb-3 space-y-2">
                                                        <p className="font-medium">
                                                            Send code via
                                                        </p>
                                                        {availableChannels.map(
                                                            (channel) => (
                                                                <label
                                                                    key={
                                                                        channel
                                                                    }
                                                                    className="mr-4 inline-flex items-center gap-2"
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name="otp_channel"
                                                                        value={
                                                                            channel
                                                                        }
                                                                        checked={
                                                                            otpChannel ===
                                                                            channel
                                                                        }
                                                                        disabled={
                                                                            formBusy
                                                                        }
                                                                        onChange={() =>
                                                                            setOtpChannel(
                                                                                channel,
                                                                            )
                                                                        }
                                                                    />
                                                                    {channel ===
                                                                    "sms"
                                                                        ? "SMS"
                                                                        : "Email"}
                                                                </label>
                                                            ),
                                                        )}
                                                    </div>
                                                )}

                                                <div className="mb-3 flex flex-wrap gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={sendClaimOtp}
                                                        disabled={
                                                            formBusy ||
                                                            !matchToken
                                                        }
                                                        className="rounded-lg border border-primary bg-primary px-4 py-2 text-white disabled:opacity-50"
                                                    >
                                                        {otpSent
                                                            ? "Resend code"
                                                            : "Send code"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={
                                                            resetClaimState
                                                        }
                                                        disabled={formBusy}
                                                        className="rounded-lg border border-stroke px-4 py-2 dark:border-strokedark"
                                                    >
                                                        Use different contact
                                                    </button>
                                                </div>

                                                {otpSent && (
                                                    <div className="mb-2">
                                                        <label className="mb-1 block font-medium">
                                                            Verification code
                                                        </label>
                                                        <div className="flex flex-wrap gap-2">
                                                            <input
                                                                type="text"
                                                                inputMode="numeric"
                                                                autoComplete="one-time-code"
                                                                value={otpCode}
                                                                disabled={
                                                                    formBusy
                                                                }
                                                                onChange={(
                                                                    e,
                                                                ) =>
                                                                    setOtpCode(
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                className="w-40 rounded-lg border border-stroke bg-transparent px-3 py-2 dark:border-form-strokedark dark:bg-form-input"
                                                                placeholder="6-digit code"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={
                                                                    verifyClaimOtp
                                                                }
                                                                disabled={
                                                                    formBusy ||
                                                                    !otpCode
                                                                }
                                                                className="rounded-lg border border-primary bg-primary px-4 py-2 text-white disabled:opacity-50"
                                                            >
                                                                Verify
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {claimStep === "verified" && (
                                            <p className="text-body">
                                                Identity verified. Complete the
                                                form below to claim your
                                                profile.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {(claimError || errors.email) && claimStep && (
                                    <InputError
                                        message={claimError || errors.email}
                                        className="mb-4 text-red"
                                    />
                                )}

                                <form onSubmit={submit}>
                                    <div className="mb-4">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">
                                            Username
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="username"
                                                name="username"
                                                value={data.username}
                                                disabled={formBusy}
                                                onChange={(e) =>
                                                    setData(
                                                        "username",
                                                        e.target.value,
                                                    )
                                                }
                                                type="text"
                                                placeholder="Enter a username"
                                                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                            />
                                            <InputError
                                                message={errors.username}
                                                className="mt-2 text-red"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">
                                            Email
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="email"
                                                type="email"
                                                name="email"
                                                value={data.email}
                                                disabled={
                                                    formBusy ||
                                                    Boolean(claimStep)
                                                }
                                                onChange={(e) => {
                                                    resetClaimState();
                                                    setData(
                                                        "email",
                                                        e.target.value,
                                                    );
                                                }}
                                                placeholder="Enter your email"
                                                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                            />
                                            <InputError
                                                message={
                                                    !claimStep
                                                        ? claimError ||
                                                          errors.email
                                                        : errors.email
                                                }
                                                className="mt-2 text-red"
                                            />
                                        </div>
                                        <div className="mt-2 flex items-center">
                                            <input
                                                id="newsletter"
                                                name="newsletter"
                                                type="checkbox"
                                                checked={data.newsletter}
                                                disabled={formBusy}
                                                onChange={(e) =>
                                                    setData(
                                                        "newsletter",
                                                        e.target.checked,
                                                    )
                                                }
                                                className="mr-2"
                                            />
                                            <label
                                                htmlFor="newsletter"
                                                className="text-sm text-black dark:text-white"
                                            >
                                                Subscribe to our newsletter
                                            </label>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">
                                            First Name{" "}
                                            <span className="text-sm text-gray-500">
                                                (Optional)
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="first_name"
                                                name="first_name"
                                                type="text"
                                                value={data.first_name}
                                                disabled={formBusy}
                                                onChange={(e) =>
                                                    setData(
                                                        "first_name",
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Enter your first name"
                                                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                            />
                                            <InputError
                                                message={errors.first_name}
                                                className="mt-2 text-red"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">
                                            Last Name{" "}
                                            <span className="text-sm text-gray-500">
                                                (Optional)
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="last_name"
                                                name="last_name"
                                                type="text"
                                                value={data.last_name}
                                                disabled={formBusy}
                                                onChange={(e) =>
                                                    setData(
                                                        "last_name",
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Enter your last name"
                                                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                            />
                                            <InputError
                                                message={errors.last_name}
                                                className="mt-2 text-red"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="password"
                                                type="password"
                                                name="password"
                                                value={data.password}
                                                disabled={formBusy}
                                                onChange={(e) =>
                                                    setData(
                                                        "password",
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Enter your password"
                                                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                            />
                                            <InputError
                                                message={errors.password}
                                                className="mt-2 text-red"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-6">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">
                                            Re-type Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="password"
                                                placeholder="Re-enter your password"
                                                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                                id="password_confirmation"
                                                name="password_confirmation"
                                                value={
                                                    data.password_confirmation
                                                }
                                                disabled={formBusy}
                                                onChange={(e) =>
                                                    setData(
                                                        "password_confirmation",
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                            <InputError
                                                message={
                                                    errors.password_confirmation
                                                }
                                                className="mt-2"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">
                                            Date of Birth
                                        </label>
                                        <input
                                            type="date"
                                            id="dob"
                                            name="dob"
                                            disabled={formBusy}
                                            className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                            value={data.dob}
                                            onChange={(e) => {
                                                setData("dob", e.target.value);
                                                setDobError("");
                                            }}
                                        />
                                        {(dobError || errors.dob) && (
                                            <div className="mt-2 text-red">
                                                {dobError || errors.dob}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-4">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">
                                            Phone Number{" "}
                                            <span className="text-sm text-gray-500">
                                                (Optional)
                                            </span>
                                        </label>
                                        <PhoneInput
                                            id="phone_number"
                                            name="phone_number"
                                            value={data.phone_number}
                                            disabled={
                                                formBusy || Boolean(claimStep)
                                            }
                                            onChange={(e164) => {
                                                resetClaimState();
                                                setData("phone_number", e164);
                                            }}
                                        />
                                        <InputError
                                            message={errors.phone_number}
                                            className="mt-2 text-red"
                                        />
                                    </div>

                                    <InputError
                                        message={errors.is_enabled}
                                        className="mt-2 text-red"
                                    />

                                    <div className="mb-5">
                                        <button
                                            type="submit"
                                            className={`w-full cursor-pointer rounded-lg border bg-primary p-4 text-white transition hover:bg-opacity-90 ${
                                                formBusy
                                                    ? "cursor-not-allowed opacity-50"
                                                    : ""
                                            }`}
                                            disabled={
                                                formBusy ||
                                                claimStep === "found"
                                            }
                                        >
                                            {formBusy
                                                ? "Processing..."
                                                : submitLabel}
                                        </button>
                                        {claimStep === "found" && (
                                            <p className="mt-2 text-center text-sm text-body">
                                                Verify the code above to
                                                continue.
                                            </p>
                                        )}
                                    </div>

                                    <div className="mt-6 text-center">
                                        <p>
                                            Already have an account?{" "}
                                            <Link
                                                href="/login"
                                                className="text-primary"
                                            >
                                                Sign in
                                            </Link>
                                        </p>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DefaultLayout>
    );
}
